/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { uniq } from "lodash"
import { Drawer, Button, Row, Col } from "@duxton-web/ui"
import { Form } from "grab-ui"

import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { ServiceHourList } from "components/list/ServiceHourList"
import { SellingTime } from "types/models/menu"
import { SellingTimeDropdown } from "components/dropdowns"
import {
  getSellingTimeByID,
  getSellingTimeMetadata,
} from "utils/selling_time_helper"
import { EventAction, EventCategory } from "constants/tracking"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { AnyAction } from "redux"
import { useMenuItemSearch } from "hooks/useMenuItemSearch"
import { sellingTimeDrawer } from "./styles"
import { drawerFooter } from "../styles"

export interface MultiSelectSellingTimeDrawerProps extends FormComponentProps {
  visible: boolean
  onClose?: () => void
}

export const MultiSelectSellingTimeDrawerComponent: FC<MultiSelectSellingTimeDrawerProps> = ({
  visible,
  onClose,
}: MultiSelectSellingTimeDrawerProps) => {
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    drawer: { itemSelections },
    isEditAllowed,
    items,
    sellingTimes,
  } = useSelector((state: AppState) => state.menu)
  const dispatch = useDispatch()

  const { refetchSearch } = useMenuItemSearch()

  const textMap = useTranslationsMap({
    sellingTimeLabel: "menu.sellingTime.label.sellingTime",
    saveChangesButton: "menu.buttons.saveChanges",
    updateSuccessMessage: "menu.sellingTime.toast.success.update",
    cancelButton: "menu.buttons.cancel",
  })

  const [selectedSellingTime, setSelectedSellingTime] = useState<
    SellingTime | undefined
  >()

  const count = Object.values(itemSelections ?? {}).reduce(
    (acc: number, selected: boolean) => acc + (selected ? 1 : 0),
    0,
  )

  const selectedItemIDs = Object.keys(itemSelections ?? {}).filter(
    itemID => itemSelections?.[itemID] === true,
  )

  const selectedSellingTimeIDs = uniq(
    items
      .filter(({ itemID }) => selectedItemIDs.includes(itemID ?? ""))
      .map(({ sellingTimeID }) => sellingTimeID),
  )

  const hasDefaultSelectedSellingTime = selectedSellingTimeIDs.length === 1
  const defaultSelectedSellingTime = getSellingTimeByID(
    selectedSellingTimeIDs[0],
    sellingTimes,
  )

  useEffect(() => {
    if (visible) {
      if (hasDefaultSelectedSellingTime) {
        // if selected items has the same selling time, set default selected selling time
        setSelectedSellingTime(defaultSelectedSellingTime)
      } else {
        setSelectedSellingTime(undefined)
      }
    }
  }, [visible, hasDefaultSelectedSellingTime, defaultSelectedSellingTime])

  useEffect(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewModifyItem,
      action: EventAction.pageView,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ...(defaultSelectedSellingTime && {
          SELLING_TIME_INFO: defaultSelectedSellingTime,
        }),
        HAS_SELLING_TIME: hasDefaultSelectedSellingTime,
        ITEM_ID: Object.keys(itemSelections || {}).map(key => key),
        NUM_ITEM: Object.keys(itemSelections || {}).map(key => key).length,
      },
    })
  }, [])

  const getSellingTimeInfoEventLabels = () => {
    const previousSellingTime = hasDefaultSelectedSellingTime
      ? defaultSelectedSellingTime
      : null
    const newSellingTime = selectedSellingTime
    return {
      PREVIOUS_SELLING_TIME_INFO: previousSellingTime
        ? getSellingTimeMetadata(previousSellingTime)
        : null,
      SELLING_TIME_INFO: newSellingTime
        ? getSellingTimeMetadata(newSellingTime)
        : null,
    }
  }

  const handleClickSave = async () => {
    if (selectedSellingTime?.sellingTimeID) {
      const resp: any = (await dispatch(
        actionCreator.menu.updateItemsSellingTime({
          merchantID,
          menuGroupID,
          merchantGroupID,
          itemIDs: selectedItemIDs,
          sellingTimeID: selectedSellingTime.sellingTimeID,
          successMessage: textMap.updateSuccessMessage,
        }),
      )) as AnyAction

      // Refresh search results if there's an active search
      await refetchSearch()

      let labels: any = {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ...getSellingTimeInfoEventLabels(),
        ITEM_ID: Object.keys(itemSelections || {}).map(key => key),
        NUM_ITEM: Object.keys(itemSelections || {}).map(key => key).length,
        STATUS: "SUCCESS",
      }
      if (resp?.type.endsWith("_FAIL")) {
        labels = {
          ...labels,
          STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewModifyItem,
        action: EventAction.submitModifyItem,
        labels,
      })
    }
  }

  return (
    <Fragment>
      <Drawer
        title={useTranslation(
          count === 1
            ? "menu.overview.itemSelected"
            : "menu.overview.itemsSelected",
          { "<x>": `${count}` },
        )}
        width={450}
        open={visible}
        onClose={onClose}
        destroyOnClose
      >
        <div css={sellingTimeDrawer}>
          <Row
            css={css`
              flex-direction: column;
            `}
          >
            <Form.Item label={`${textMap.sellingTimeLabel} *`}>
              <SellingTimeDropdown
                data-testid="selling-time-dropdown"
                disabled={!isEditAllowed}
                onChange={setSelectedSellingTime}
                selectedSellingTime={selectedSellingTime}
                sellingTimes={sellingTimes}
                eventTrackerObject={{
                  eventCategoryTracker: EventCategory.menuOverviewModifyItem,
                  itemIDs: Object.keys(itemSelections || {}).map(key => key),
                  isMultipleSelect: true,
                }}
              />
            </Form.Item>
            <ServiceHourList sellingTime={selectedSellingTime} />
          </Row>
        </div>
        <div css={drawerFooter}>
          <Row gutter={12}>
            <Col span={6}>
              <Button block onClick={onClose}>
                {textMap.cancelButton}
              </Button>
            </Col>
            <Col flex="auto">
              <Button
                data-testid="save-button"
                type="primary"
                onClick={handleClickSave}
                block
                disabled={
                  (hasDefaultSelectedSellingTime &&
                    defaultSelectedSellingTime?.sellingTimeID ===
                      selectedSellingTime?.sellingTimeID) ||
                  !selectedSellingTime
                }
              >
                {textMap.saveChangesButton}
              </Button>
            </Col>
          </Row>
        </div>
      </Drawer>
    </Fragment>
  )
}
export const MultiSelectSellingTimeDrawer = Form.create<
  MultiSelectSellingTimeDrawerProps
>({})(MultiSelectSellingTimeDrawerComponent)
